import { useState, useEffect, Fragment } from "react";
import { Switch, Dialog, Transition, TransitionChild, DialogPanel, DisclosureButton, Disclosure, DisclosurePanel } from '@headlessui/react';
import ModalInfo from "../../../components/lib/modalInfo";
import ClientService from "../../../rest/clientService";
import { GrCircleQuestion } from "react-icons/gr";
import { BiChevronDown } from "react-icons/bi";

const ConfigSection = ({ title, count, children, defaultOpen = false }) => (
    <Disclosure defaultOpen={defaultOpen} as="div" className="bg-white rounded-2xl shadow border border-gray-100 overflow-hidden mb-4">
        {({ open }) => (
            <>
                <DisclosureButton className="w-full px-6 py-5 flex justify-between items-center bg-white hover:bg-gray-50 transition-colors cursor-pointer outline-none">
                    <div className="flex flex-col items-start text-left">
                        <span className="text-lg font-bold text-custom_gray_font">{title}</span>
                        {count !== undefined && (
                            <span className="text-xs text-gray-400 font-medium">{count} cadastrados</span>
                        )}
                    </div>
                    <BiChevronDown
                        className={`${open ? 'transform rotate-180' : ''} w-6 h-6 text-gray-400 transition-transform duration-200 pointer-events-none`}
                    />
                </DisclosureButton>
                
                <Transition
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                >
                    <DisclosurePanel 
                        className="px-6 pb-6 pt-2 border-t border-gray-100 bg-gray-50/50 cursor-default"
                        onClick={(e) => e.stopPropagation()} 
                    >
                        {children}
                    </DisclosurePanel>
                </Transition>
            </>
        )}
    </Disclosure>
);


const ConfigurationsTab = ({ clientPreferences, setClientPreferences, turns, setTurns }) => {
    
    const cryptoRandomId = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;

    const [isTurnModalOpen, setIsTurnModalOpen] = useState(false);
    const [editingTurn, setEditingTurn] = useState(null);
    const [turnForm, setTurnForm] = useState({ name: "", startTime: "", endTime: "", clientCode: "" });
    const [turnErrors, setTurnErrors] = useState({ name: "", startTime: "", endTime: "" });
    const [disableGuard, setDisableGuard] = useState(null);

    const handleOpenTurnModal = (turn) => {
        if (turn) {
            setEditingTurn(turn);
            setTurnForm({ name: turn.name, startTime: turn.startTime, endTime: turn.endTime, clientCode: turn.clientCode });
        } else {
            setEditingTurn(null);
            setTurnForm({ name: "", startTime: "", endTime: "", clientCode: sessionStorage.getItem('clientCode') || null });
        }
        setIsTurnModalOpen(true);
    };

    const handleSaveTurnModal = () => {
        if (!turnForm.name || !turnForm.startTime || !turnForm.endTime) return; // Validação simples
        if (editingTurn) {
            setTurns(prev => prev.map(t => t.turn_id === editingTurn.turn_id ? { ...t, ...turnForm } : t));
        } else {
            const newTurn = { turn_id: `tmp-${cryptoRandomId()}`, isActive: true, ...turnForm, clientCode: turnForm.clientCode || sessionStorage.getItem('clientCode') };
            setTurns(prev => [...prev, newTurn]);
        }
        setIsTurnModalOpen(false);
    };

    const handleToggleTurnActive = async (turnId) => {
        const turn = turns.find(t => t.turn_id === turnId);
        if (!turn) return;

        if (turn.isActive) {
            const count = await ClientService.countActiveStudentsForTurn(turnId).catch(() => 0);
            if (count > 0) {
                setDisableGuard({ turnId, count });
                return;
            }
        }

        setTurns(prev => prev.map(t => t.turn_id === turnId ? { ...t, isActive: !t.isActive } : t));
    };

    const handleDeleteTurn = (turnId) => setTurns(prev => prev.filter(t => t.turn_id !== turnId));

    const PAYMENT_MAP = { "Crédito Foodz": "CREDITO FOODZ", "Dinheiro": "DINHEIRO", "PIX": "PIX", "VA | VR": "VA | VR", "Cartão Crédito": "CREDITO", "Cartão Débito": "DEBITO" };
    const ALL_PAYMENT_METHODS = Object.keys(PAYMENT_MAP);
    const [enabledPayments, setEnabledPayments] = useState([]);

    useEffect(() => {
        if (clientPreferences?.accepted_payments) {
            setEnabledPayments(clientPreferences.accepted_payments.split(";").map(s => s.trim().toUpperCase()).filter(Boolean));
        }
    }, [clientPreferences]);

    const togglePayment = (label) => {
        const value = PAYMENT_MAP[label];
        if (!value || value === "CREDITO FOODZ") return;
        const m = value.toUpperCase();
        const newList = enabledPayments.includes(m) ? enabledPayments.filter(x => x !== m) : [...enabledPayments, m];
        setEnabledPayments(newList.length === 0 ? ["CREDITO FOODZ"] : newList);
        setClientPreferences(prev => ({ ...prev, accepted_payments: (newList.length ? newList.join(";") + ";" : "") }));
    };

    const [grades, setGrades] = useState([]);
    const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);
    const [editingGrade, setEditingGrade] = useState(null);
    const [gradeForm, setGradeForm] = useState({ name: "" });

    useEffect(() => {
        setGrades(clientPreferences?.grades ? clientPreferences.grades.split(";").map(n => n.trim()).filter(Boolean).map(n => ({ id: cryptoRandomId(), name: n })) : []);
    }, [clientPreferences]);

    const updateLocalGrades = (arr) => {
        setGrades(arr);
        setClientPreferences(prev => ({ ...prev, grades: arr.length ? arr.map(g => g.name).join(";") + ";" : "" }));
    };

    const handleOpenGradeModal = (g) => { setEditingGrade(g); setGradeForm({ name: g ? g.name : "" }); setIsGradeModalOpen(true); };
    const handleSaveGradeModal = () => {
        const name = gradeForm.name.trim(); if (!name) return;
        if (!editingGrade) updateLocalGrades([...grades, { id: cryptoRandomId(), name }]);
        else updateLocalGrades(grades.map(g => g.id === editingGrade.id ? { ...g, name } : g)); // Simplificado para exemplo
        setIsGradeModalOpen(false);
    };
    const deleteGrade = (id) => updateLocalGrades(grades.filter(g => g.id !== id));

    const [categories, setCategories] = useState([]);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [categoryForm, setCategoryForm] = useState({ name: "" });

    useEffect(() => {
        setCategories(clientPreferences?.categories ? clientPreferences.categories.split(";").map(n => n.trim()).filter(Boolean).map(n => ({ id: cryptoRandomId(), name: n })) : []);
    }, [clientPreferences]);

    const updateLocalCategories = (arr) => {
        setCategories(arr);
        setClientPreferences(prev => ({ ...prev, categories: arr.length ? arr.map(c => c.name).join(";") + ";" : "" }));
    };

    const handleOpenCategoryModal = (c) => { setEditingCategory(c); setCategoryForm({ name: c ? c.name : "" }); setIsCategoryModalOpen(true); };
    const handleSaveCategoryModal = () => {
        const name = categoryForm.name.trim(); if (!name) return;
        if (!editingCategory) updateLocalCategories([...categories, { id: cryptoRandomId(), name }]);
        else updateLocalCategories(categories.map(c => c.id === editingCategory.id ? { ...c, name } : c));
        setIsCategoryModalOpen(false);
    };
    const deleteCategory = (id) => updateLocalCategories(categories.filter(c => c.id !== id));


    return (
        <div className="flex flex-col gap-2 pb-8">
                        <ConfigSection title="Turnos de Venda" count={turns.length} defaultOpen={true}>
                <div className="flex justify-end mb-4">
                     <button onClick={() => handleOpenTurnModal(null)} className="text-sm font-semibold text-custom_blue hover:underline">+ Adicionar Turno</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {turns.length === 0 && <p className="text-gray-400 italic text-sm">Nenhum turno configurado.</p>}
                    {turns.map((turn) => (
                        <div key={turn.turn_id} className="bg-white rounded-xl p-4 border border-gray-200 hover:border-custom_blue/30 transition shadow-sm">
                            <div className="flex justify-between items-start mb-2">
                                <span className="font-semibold text-gray-700 truncate">{turn.name}</span>
                                <Switch checked={turn.isActive} onChange={() => handleToggleTurnActive(turn.turn_id)} className={`${turn.isActive ? 'bg-green-500' : 'bg-gray-200'} relative inline-flex h-5 w-9 items-center rounded-full transition-colors`}>
                                    <span className={`${turn.isActive ? 'translate-x-4' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition-transform`} />
                                </Switch>
                            </div>
                            <div className="text-xs text-gray-500 mb-3 bg-gray-100 inline-block px-2 py-1 rounded-lg">{turn.startTime} - {turn.endTime}</div>
                            <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
                                <button onClick={() => handleOpenTurnModal(turn)} className="flex-1 text-xs font-medium text-gray-600 hover:text-custom_blue py-1">Editar</button>
                                <div className="w-px bg-gray-200 my-1"></div>
                                <button onClick={() => handleDeleteTurn(turn.turn_id)} className="flex-1 text-xs font-medium text-red-500 hover:text-red-700 py-1">Excluir</button>
                            </div>
                        </div>
                    ))}
                </div>
            </ConfigSection>

            <ConfigSection title="Métodos de Pagamento" count={enabledPayments.length}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {ALL_PAYMENT_METHODS.map(label => {
                        const value = PAYMENT_MAP[label];
                        const enabled = enabledPayments.includes(value);
                        return (
                            <div key={value} onClick={() => value !== "CREDITO FOODZ" && togglePayment(label)} className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer select-none ${enabled ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white border-gray-100 hover:bg-gray-50'} ${value === "CREDITO FOODZ" ? 'opacity-70 cursor-not-allowed' : ''}`}>
                                <span className={`text-sm font-medium ${enabled ? 'text-custom_blue' : 'text-gray-500'}`}>{label}</span>
                                <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${enabled ? 'bg-custom_blue border-custom_blue' : 'border-gray-300'}`}>
                                    {enabled && <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </ConfigSection>

            <ConfigSection title="Séries Escolares" count={grades.length}>
                 <div className="flex justify-end mb-4">
                     <button onClick={() => handleOpenGradeModal(null)} className="text-sm font-semibold text-custom_blue hover:underline">+ Adicionar Série</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                     {grades.map((grade) => (
                        <div key={grade.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 group hover:border-gray-300 transition">
                            <span className="text-sm font-medium text-gray-700 truncate">{grade.name}</span>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => handleOpenGradeModal(grade)} className="p-1 text-gray-400 hover:text-custom_blue">✎</button>
                                <button onClick={() => deleteGrade(grade.id)} className="p-1 text-gray-400 hover:text-red-500">×</button>
                            </div>
                        </div>
                     ))}
                </div>
            </ConfigSection>

            <ConfigSection title="Configurações Financeiras">
                <div className="space-y-4">
                    <div className="flex flex-col gap-1 max-w-xs">
                        <label className="text-sm font-semibold text-custom_gray_font">Valor mínimo de recarga (R$)</label>
                        <p className="text-xs text-gray-400">Responsáveis não poderão recarregar abaixo deste valor. Deixe em 0 para não definir mínimo.</p>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={clientPreferences?.min_credit_recharge_value ?? ""}
                            onChange={e => {
                                const val = e.target.value === "" ? null : parseFloat(e.target.value);
                                setClientPreferences(prev => ({ ...prev, min_credit_recharge_value: val }));
                            }}
                            placeholder="0,00"
                            className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-custom_blue"
                        />
                    </div>
                </div>
            </ConfigSection>

            <ConfigSection title="Categorias de Produtos" count={categories.length}>
                 <div className="flex justify-end mb-4">
                     <button onClick={() => handleOpenCategoryModal(null)} className="text-sm font-semibold text-custom_blue hover:underline">+ Adicionar Categoria</button>
                </div>
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                     {categories.map((cat) => (
                        <div key={cat.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 group hover:border-gray-300 transition">
                            <span className="text-sm font-medium text-gray-700 truncate">{cat.name}</span>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => handleOpenCategoryModal(cat)} className="p-1 text-gray-400 hover:text-custom_blue">✎</button>
                                <button onClick={() => deleteCategory(cat.id)} className="p-1 text-gray-400 hover:text-red-500">×</button>
                            </div>
                        </div>
                     ))}
                </div>
            </ConfigSection>

            <Transition appear show={isTurnModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsTurnModalOpen(false)}>
                    <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </TransitionChild>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl text-left">
                                    <div className="flex items-center justify-between mb-4">
                                        <Dialog.Title className="text-lg font-medium">{editingTurn ? "Editar Turno" : "Novo Turno"}</Dialog.Title>
                                        <ModalInfo icon={<GrCircleQuestion />} content="Defina horários claros para que os responsáveis saibam quando podem reservar." />
                                    </div>
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <label className="text-custom_gray_font text-sm">Nome do Turno</label>
                                            <input type="text" className="w-full px-4 py-2 border rounded-xl mt-1" value={turnForm.name} onChange={(e) => setTurnForm({ ...turnForm, name: e.target.value })} />
                                            {turnErrors.name && <p className="text-red-500 text-xs mt-1">{turnErrors.name}</p>}
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-custom_gray_font text-sm">Início</label>
                                                <input type="time" className="w-full px-4 py-2 border rounded-xl mt-1" value={turnForm.startTime} onChange={(e) => setTurnForm({ ...turnForm, startTime: e.target.value })} />
                                                {turnErrors.startTime && <p className="text-red-500 text-xs mt-1">{turnErrors.startTime}</p>}
                                            </div>
                                            <div>
                                                <label className="text-custom_gray_font text-sm">Fim</label>
                                                <input type="time" className="w-full px-4 py-2 border rounded-xl mt-1" value={turnForm.endTime} onChange={(e) => setTurnForm({ ...turnForm, endTime: e.target.value })} />
                                                {turnErrors.endTime && <p className="text-red-500 text-xs mt-1">{turnErrors.endTime}</p>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-6 flex justify-end gap-3">
                                        <button onClick={() => setIsTurnModalOpen(false)} className="px-4 py-2 rounded-xl border hover:bg-gray-50">Cancelar</button>
                                        <button onClick={handleSaveTurnModal} className="px-4 py-2 bg-custom_green text-white rounded-xl hover:bg-green-600">Salvar</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <Transition appear show={isGradeModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsGradeModalOpen(false)}>
                    <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </TransitionChild>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl text-left">
                                    <Dialog.Title className="text-lg font-medium mb-4">{editingGrade ? "Editar Série" : "Nova Série"}</Dialog.Title>
                                    <input type="text" className="w-full px-4 py-2 border rounded-xl" value={gradeForm.name} maxLength={32} onChange={(e) => setGradeForm({ name: e.target.value.slice(0, 32) })} placeholder="Nome da série" />
                                    <div className="mt-6 flex justify-end gap-3">
                                        <button onClick={() => setIsGradeModalOpen(false)} className="px-4 py-2 rounded-xl border hover:bg-gray-50">Cancelar</button>
                                        <button onClick={handleSaveGradeModal} className="px-4 py-2 bg-custom_green text-white rounded-xl hover:bg-green-600">Salvar</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <Transition appear show={!!disableGuard} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setDisableGuard(null)}>
                    <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </TransitionChild>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl text-left">
                                    <Dialog.Title className="text-lg font-semibold text-gray-800 mb-3">Não é possível desativar este turno</Dialog.Title>
                                    <p className="text-sm text-gray-600">
                                        Este turno possui <span className="font-bold text-red-500">{disableGuard?.count} {disableGuard?.count === 1 ? 'aluno ativo vinculado' : 'alunos ativos vinculados'}</span>. Remova a associação dos alunos antes de desativar o turno.
                                    </p>
                                    <div className="mt-6 flex justify-end">
                                        <button onClick={() => setDisableGuard(null)} className="px-4 py-2 bg-custom_blue text-white rounded-xl hover:bg-blue-700">Entendido</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <Transition appear show={isCategoryModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsCategoryModalOpen(false)}>
                    <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </TransitionChild>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl text-left">
                                    <Dialog.Title className="text-lg font-medium mb-4">{editingCategory ? "Editar Categoria" : "Nova Categoria"}</Dialog.Title>
                                    <input type="text" className="w-full px-4 py-2 border rounded-xl" value={categoryForm.name} maxLength={32} onChange={(e) => setCategoryForm({ name: e.target.value.slice(0, 32) })} placeholder="Nome da categoria" />
                                    <div className="mt-6 flex justify-end gap-3">
                                        <button onClick={() => setIsCategoryModalOpen(false)} className="px-4 py-2 rounded-xl border hover:bg-gray-50">Cancelar</button>
                                        <button onClick={handleSaveCategoryModal} className="px-4 py-2 bg-custom_green text-white rounded-xl hover:bg-green-600">Salvar</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

export default ConfigurationsTab;